/*
 * Vieux (ported from Liive 21d9652, b422a9a, bd83dff, ed14c20): dimension
 * brackets drawn ON the front-on product photo (snippets/vieux-pdp-specs.liquid).
 * data-width / data-lens / data-bridge / data-arm carry the product's
 * dimension metafields' .value numbers in mm.
 *
 * The photo is read into a canvas (same-origin /cdn/shop URL, also requested
 * with crossorigin="anonymous"), and the frame is found from pixels:
 *   - background = the average of the four corners;
 *   - frame band = the longest run of rows where enough pixels differ from it;
 *   - frame left / right = outermost columns with ink inside that band;
 *   - bridge = the run of background columns between the lenses, measured
 *     row by row below the bridge bar (narrow end of the nose gap).
 * The lens bracket runs from the bridge outwards for the lens's mm on the
 * photo's own scale (frame px / total width mm).
 *
 * Every result is checked against the product's own mm values before
 * anything is drawn. If the canvas is tainted, the photo is unusual, or the
 * checks disagree, the photo stays as it is (the measurements are still in
 * the Frame rows above it): never misplaced brackets.
 *
 * Re-runs after a variant change (assets/vieux-variant-refresh.js dispatches
 * vieux:variant-refreshed).
 */

const FG_DIFF = 30; // channel difference from the background that counts as frame

/** @param {HTMLImageElement} img */
function analyse(img) {
  const W = img.naturalWidth;
  const H = img.naturalHeight;
  if (!W || !H) return null;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  /** @type {Uint8ClampedArray} */
  let px;
  try {
    px = ctx.getImageData(0, 0, W, H).data; // throws when tainted
  } catch (e) {
    return null;
  }

  // Background from the four corners (8x8 each).
  let br = 0, bg = 0, bb = 0, n = 0;
  for (const [cx, cy] of [[0, 0], [W - 8, 0], [0, H - 8], [W - 8, H - 8]]) {
    for (let y = cy; y < cy + 8; y++) for (let x = cx; x < cx + 8; x++) {
      const i = (y * W + x) * 4;
      br += px[i]; bg += px[i + 1]; bb += px[i + 2]; n++;
    }
  }
  br /= n; bg /= n; bb /= n;

  const fg = new Uint8Array(W * H);
  const rowFg = new Uint32Array(H);
  for (let y = 0; y < H; y++) {
    let c = 0;
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      const d = Math.max(Math.abs(px[i] - br), Math.abs(px[i + 1] - bg), Math.abs(px[i + 2] - bb));
      if (d > FG_DIFF) { fg[y * W + x] = 1; c++; }
    }
    rowFg[y] = c;
  }

  // Frame band: longest run of rows with >= 12% ink (gaps of up to 3 rows bridged).
  let best = [0, -1], start = -1, gap = 0;
  for (let y = 0; y <= H; y++) {
    const on = y < H && rowFg[y] >= W * 0.12;
    if (on) {
      if (start < 0) start = y;
      gap = 0;
    } else if (start >= 0) {
      gap++;
      if (gap > 3 || y === H) {
        const end = y - gap;
        if (end - start > best[1] - best[0]) best = [start, end];
        start = -1; gap = 0;
      }
    }
  }
  const top = best[0], bottom = best[1];
  const bandH = bottom - top + 1;
  if (bandH < H * 0.08) return null;

  // Left / right extent inside the band.
  const colMin = Math.max(3, bandH * 0.04);
  let left = -1, right = -1;
  for (let x = 0; x < W; x++) {
    let c = 0;
    for (let y = top; y <= bottom; y++) c += fg[y * W + x];
    if (c >= colMin) { if (left < 0) left = x; right = x; }
  }
  const fw = right - left;
  if (left < 0 || fw < W * 0.4) return null;
  // Touching the image edge means the frame is cut off: not measurable.
  if (left < 2 || right > W - 3) return null;

  // Bridge: per row below the bridge bar, the background run around the centre.
  const c0 = Math.round((left + right) / 2);
  const reach = Math.round(fw * 0.2);
  const gaps = [];
  for (let y = Math.round(top + bandH * 0.3); y <= Math.round(bottom - bandH * 0.2); y++) {
    if (fg[y * W + c0]) continue; // ink at the centre: the bridge bar or a nose pad
    let lx = -1, rx = -1;
    for (let x = c0; x >= c0 - reach; x--) if (fg[y * W + x]) { lx = x; break; }
    for (let x = c0; x <= c0 + reach; x++) if (fg[y * W + x]) { rx = x; break; }
    if (lx >= 0 && rx >= 0) gaps.push([rx - lx, lx, rx]);
  }
  if (gaps.length < Math.max(4, bandH * 0.08)) return null;
  gaps.sort((a, b) => a[0] - b[0]);
  const g = gaps[Math.floor(gaps.length * 0.2)]; // narrow end, ignoring single-row noise
  return { W, H, top, bottom, left, right, bridgeL: g[1] + 1, bridgeR: g[2] };
}

const pct = (v, total) => `${((v / total) * 100).toFixed(3)}%`;

/** @param {HTMLElement} fig */
function draw(fig) {
  const img = /** @type {HTMLImageElement|null} */ (fig.querySelector('.vieux-dims__img'));
  const stage = /** @type {HTMLElement|null} */ (fig.querySelector('.vieux-dims__stage'));
  const marks = /** @type {HTMLElement|null} */ (fig.querySelector('.vieux-dims__marks'));
  if (!img || !stage || !marks) return;

  const mm = (k) => parseFloat(fig.dataset[k] || '') || 0;
  const width = mm('width'), lens = mm('lens'), bridge = mm('bridge'), arm = mm('arm');
  const fail = (why) => { fig.dataset.vieuxDimsState = 'photo-only'; fig.dataset.vieuxDimsWhy = why; };
  if (!width) return fail('no total width');

  const a = analyse(img);
  if (!a) return fail('analysis');

  const fw = a.right - a.left;
  const scale = fw / width; // px per mm
  const bw = a.bridgeR - a.bridgeL;
  const bc = (a.bridgeL + a.bridgeR) / 2;
  const fc = (a.left + a.right) / 2;
  // Checks against the product's own numbers.
  if (Math.abs(bc - fc) > fw * 0.05) return fail('bridge off centre');
  if (bridge && (bw < bridge * scale * 0.5 || bw > bridge * scale * 2)) return fail('bridge size');
  if (!bridge && (bw < fw * 0.03 || bw > fw * 0.2)) return fail('bridge size');
  const lensL = a.bridgeL - lens * scale;
  const lensR2 = a.bridgeR + lens * scale;
  if (lens && (lensL < a.left - fw * 0.02 || lensR2 > a.right + fw * 0.02)) return fail('lens wider than frame');
  // The lens bracket runs from the bridge outwards for the stated lens mm. When
  // the stated sizes leave more than 11% of the width per side for rim and
  // hinge, the bracket would end visibly short of the lens on the photo, so it
  // is left off (the value row still says the lens width).
  const rim = (width - 2 * lens - (bridge || 0)) / 2 / width;
  const showLens = lens > 0 && (!bridge || rim <= 0.11);
  if (lens && !showLens) fig.dataset.vieuxDimsLens = 'omitted';

  // Crop: the frame plus a little air either side; the band plus room for marks.
  const padX = fw * 0.05;
  const padY = (a.bottom - a.top) * 0.14;
  const x0 = Math.max(0, a.left - padX);
  const x1 = Math.min(a.W, a.right + padX);
  const y0 = Math.max(0, a.top - padY);
  const y1 = Math.min(a.H, a.bottom + padY);
  const cw = x1 - x0, ch = y1 - y0;

  stage.style.aspectRatio = `${cw} / ${ch}`;
  img.style.position = 'absolute';
  img.style.objectFit = 'fill';
  img.style.width = pct(a.W, cw);
  img.style.height = pct(a.H, ch);
  img.style.left = pct(-x0, cw);
  img.style.top = pct(-y0, ch);
  img.style.maxWidth = 'none';

  const X = (v) => pct(v - x0, cw);
  const W = (v) => pct(v, cw);
  const parts = [];
  parts.push(`<span class="vieux-dims__bracket vieux-dims__bracket--top" style="left:${X(a.left)};width:${W(fw)}"><span class="vieux-dims__tag">Total width ${width} mm</span></span>`);
  if (showLens) {
    parts.push(`<span class="vieux-dims__bracket vieux-dims__bracket--bottom" style="left:${X(lensL)};width:${W(lens * scale)}"><span class="vieux-dims__tag">Lens ${lens} mm</span></span>`);
  }
  if (bridge) {
    parts.push(`<span class="vieux-dims__bracket vieux-dims__bracket--bottom${showLens ? ' vieux-dims__bracket--bridge' : ''}" style="left:${X(a.bridgeL)};width:${W(bw)}"><span class="vieux-dims__tag">Bridge ${bridge} mm</span></span>`);
  }
  if (arm) {
    const armCentre = showLens ? a.bridgeR + (lens * scale) / 2 : (a.bridgeR + a.right) / 2;
    parts.push(`<span class="vieux-dims__arm" style="left:${X(armCentre)}">Arm ${arm} mm</span>`);
  }
  marks.innerHTML = parts.join('');
  marks.hidden = false;
  fig.dataset.vieuxDimsState = 'brackets';
  fig.dataset.vieuxDimsPx = JSON.stringify({ left: a.left, right: a.right, top: a.top, bottom: a.bottom, bridgeL: a.bridgeL, bridgeR: a.bridgeR, W: a.W });
}

/** @param {ParentNode} root */
function scan(root) {
  root.querySelectorAll('[data-vieux-dims]').forEach((el) => {
    const fig = /** @type {HTMLElement} */ (el);
    if (fig.dataset.vieuxDimsState) return;
    const img = /** @type {HTMLImageElement|null} */ (fig.querySelector('.vieux-dims__img'));
    if (!img) return;
    const run = () => {
      try {
        draw(fig);
      } catch (e) {
        fig.dataset.vieuxDimsState = 'photo-only';
      }
    };
    if (img.complete && img.naturalWidth) run();
    else {
      img.addEventListener('load', run, { once: true });
      img.addEventListener('error', () => { fig.dataset.vieuxDimsState = 'photo-only'; }, { once: true });
    }
  });
}

if (!window.__vieuxDims) {
  window.__vieuxDims = true;
  scan(document);
  document.addEventListener('vieux:variant-refreshed', (event) => {
    const el = /** @type {any} */ (event).detail?.element;
    scan(el instanceof HTMLElement ? el : document);
  });
  // Theme editor: a section / block is re-rendered in place without a page
  // load, so the new photo would never be measured. Scan again (figures that
  // are already measured carry data-vieux-dims-state and are skipped).
  const rescan = (event) => {
    const el = event.target;
    scan(el instanceof HTMLElement ? el : document);
  };
  ['shopify:section:load', 'shopify:block:select', 'shopify:section:select'].forEach((name) =>
    document.addEventListener(name, rescan)
  );
}
